import { useEffect, useRef, useState } from 'react';

import { CTA, TapPaymentWidget } from '@/components/atomic/atoms';
import {
  EditPromoCode,
  ExternalNote,
  InputPromoCode
} from '@/components/atomic/molecules';
import { Text, TextKeyValuePair } from '@/components/atomic/nuclei';
import PriceSummaryOrganism from '@/components/atomic/organism/hostCart/PriceSummaryOrganism';
import config from '@/config';
import { GetUiDeviceSurface } from '@/helpers/screenResolutionHelper';
import { parseFormatPriceValueFromAPI } from '@/lib/numberStringUtils';

const CartSummaryTemplateMobile = ({
  code,
  isCartEditable,
  isDefaultCart,
  note,
  onProceedForCheckout,
  orderTotal,
  priceSummaryList,
  proceedToCheckoutAndPaymentDisabled,
  processApplyPromoCode,
  processRemovePromoCode,
  promoCodeDiscountAmount,
  promoCodeError,
  renderTapPaymentWidget,
  setLoading,
  setPromoCodeError,
  setRenderTapPaymentWidget,
  setShowToast,
  setTapSanitizedToken,
  userCart
}) => {
  const {
    email: hostEmail,
    mobile: hostMobile,
    name: hostName
  } = userCart || {};

  const [isOpen, setIsOpen] = useState(false);

  // TODO: HP - try to apply class using DOM contex provider
  useEffect(() => {
    const addOrRemove = isOpen ? 'add' : 'remove';
    document.body.classList[`${addOrRemove}`]('overflow-hidden');
  }, [isOpen]);

  const mobileSummaryRef = useRef(null);

  return (
    <div>
      {note && (
        <div className='border border-platinum rounded-2.5 p-3 mb-4'>
          <ExternalNote {...{ note }} />
        </div>
      )}
      <div className='w-full md:w-95 border border-platinum rounded-2.5 p-3 mb-12'>
        {isCartEditable && (
          <>
            <Text
              {...{ content: 'Promo Code', className: 'font-medium mb-4' }}
            />
            {promoCodeDiscountAmount ? (
              <EditPromoCode
                {...{
                  promoCode: code,
                  promoCodeAmount: parseFormatPriceValueFromAPI(
                    promoCodeDiscountAmount
                  ),
                  removePromoCode: processRemovePromoCode
                }}
              />
            ) : (
              <InputPromoCode
                {...{
                  derivedValues: { promoCode: { code } },
                  processApplyPromoCode,
                  promoCodeError,
                  setPromoCodeError
                }}
              />
            )}
            <div
              ref={mobileSummaryRef}
              className='my-6 border-t border-platinum'
            ></div>
          </>
        )}
        <PriceSummaryOrganism {...{ priceSummaryList }} />
      </div>
      <div className='flex w-full justify-between bg-white px-6 fixed z-20 bottom-0 left-0 py-3 border-t'>
        <div className='flex flex-col gap-1 w-1/2 -mt-2'>
          <a
            onClick={() =>
              mobileSummaryRef?.current?.scrollIntoView({ behavior: 'smooth' })
            }
          >
            <TextKeyValuePair
              {...{
                label: `AED ${parseFormatPriceValueFromAPI(orderTotal)}`,
                labelClass: 'text-sm font-semibold',
                value: 'View price details',
                valueClassName:
                  'text-xs font-medium text-light-purple cursor-pointer'
              }}
            />
          </a>
        </div>
        <div className='w-1/2'>
          <CTA
            {...{
              buttonColor: 'bg-brand-gradient text-white',
              children: isDefaultCart ? 'Checkout' : 'Proceed To Payment',
              className: 'w-full py-3 text-sm font-medium',
              disabled: proceedToCheckoutAndPaymentDisabled,
              onClick: () => {
                if (!config.isRenderTapPaymentWidget || isDefaultCart) {
                  onProceedForCheckout();
                } else {
                  setIsOpen(true);
                  setRenderTapPaymentWidget(true);
                }
              }
            }}
          />
        </div>
      </div>
      {isOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-70 pointer-events-none z-30' />
      )}
      {renderTapPaymentWidget && (
        <div className='fixed left-0 right-0 bottom-0 z-50 flex justify-center items-end bg-transparent'>
          <div className='w-full max-w-md bg-white rounded-t-lg p-4 shadow-xl'>
            <TapPaymentWidget
              {...{
                buttonBackgroundColorClass: 'bg-brand-gradient',
                hostEmail,
                hostMobile,
                hostName,
                onClickPayAmount: onProceedForCheckout,
                setIsOpen,
                setLoading,
                setRenderTapPaymentWidget,
                setShowToast,
                setTapSanitizedToken,
                showHeader: true,
                textColorClass: 'text-white',
                transactionAmountInUpperDenomination:
                  parseFormatPriceValueFromAPI(orderTotal)
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CartSummaryTemplateWeb = ({
  code,
  isCartEditable,
  isDefaultCart,
  note,
  onProceedForCheckout,
  orderTotal,
  priceSummaryList,
  proceedToCheckoutAndPaymentDisabled,
  processApplyPromoCode,
  processRemovePromoCode,
  promoCodeDiscountAmount,
  promoCodeError,
  renderTapPaymentWidget,
  setLoading,
  setPromoCodeError,
  setRenderTapPaymentWidget,
  setShowToast,
  setTapSanitizedToken,
  userCart
}) => {
  const {
    email: hostEmail,
    mobile: hostMobile,
    name: hostName
  } = userCart || {};

  return (
    <div className='w-full md:w-95 border border-platinum rounded-2.5 p-6 self-start min-h-0 bg-lightgray'>
      {isCartEditable && (
        <>
          <Text {...{ content: 'Promo Code', className: 'font-medium mb-4' }} />
          {promoCodeDiscountAmount ? (
            <EditPromoCode
              {...{
                promoCode: code,
                promoCodeAmount: parseFormatPriceValueFromAPI(
                  promoCodeDiscountAmount
                ),
                removePromoCode: processRemovePromoCode
              }}
            />
          ) : (
            <InputPromoCode
              {...{
                derivedValues: { promoCode: { code } },
                processApplyPromoCode,
                promoCodeError,
                setPromoCodeError
              }}
            />
          )}
          <div className='my-6 border-t border-platinum'></div>
        </>
      )}
      <PriceSummaryOrganism {...{ priceSummaryList }} />
      {config.isRenderTapPaymentWidget && (
        <div className='h-px bg-brand-gradient w-full'></div>
      )}
      {renderTapPaymentWidget ? (
        <TapPaymentWidget
          {...{
            buttonBackgroundColorClass: 'bg-brand-gradient',
            hostEmail,
            hostMobile,
            hostName,
            onClickPayAmount: onProceedForCheckout,
            setLoading,
            setShowToast,
            setTapSanitizedToken,
            textColorClass: 'text-white',
            transactionAmountInUpperDenomination:
              parseFormatPriceValueFromAPI(orderTotal)
          }}
        />
      ) : (
        <CTA
          {...{
            buttonColor: 'bg-brand-gradient p-10 text-white',
            children: isDefaultCart ? 'Checkout' : 'Proceed To Payment',
            className: 'w-full py-3',
            disabled: proceedToCheckoutAndPaymentDisabled,
            onClick: () => {
              if (!config.isRenderTapPaymentWidget || isDefaultCart) {
                onProceedForCheckout();
              } else {
                setRenderTapPaymentWidget(true);
              }
            }
          }}
        />
      )}
      <ExternalNote {...{ note }} />
    </div>
  );
};

const CartSummaryMap = {
  mobile: CartSummaryTemplateMobile,
  web: CartSummaryTemplateWeb
};

const CartSummaryTemplate = ({
  additionalChargeAmount,
  deliveryChargeAmount,
  isCartEditable = true,
  isDefaultCart,
  itemsForPriceSummary,
  listedDiscountAmount,
  listedPriceAmount,
  lumpSumDiscount,
  nonTaxableCharge,
  note,
  onProceedForCheckout,
  orderTotal,
  preVatAmount,
  processApplyPromoCode,
  processRemovePromoCode,
  promoCode,
  promoCodeDiscountAmount,
  promoCodeError,
  renderTapPaymentWidget,
  sellingPriceAmount,
  setLoading,
  setPromoCodeError,
  setRenderTapPaymentWidget,
  setShowToast,
  setTapSanitizedToken,
  tapSanitizedToken,
  unavailableCartItem,
  userCart,
  vatAmount
}) => {
  const surface = GetUiDeviceSurface();
  const CartSummary = CartSummaryMap[surface];

  const proceedToCheckoutAndPaymentDisabled =
    !itemsForPriceSummary.length || Boolean(unavailableCartItem.length);

  const priceSummaryList = [
    {
      dbName: 'listedPriceAmount',
      isVisible: Boolean(listedDiscountAmount),
      title: 'Sub Total (AED)',
      value: parseFormatPriceValueFromAPI(listedPriceAmount)
    },
    {
      dbName: 'listedDiscountAmount',
      isVisible: Boolean(listedDiscountAmount),
      title: 'Item Discounts (AED)',
      value: `- ${parseFormatPriceValueFromAPI(listedDiscountAmount)}`,
      valueClass: 'text-green font-medium'
    },
    {
      dbName: 'sellingPriceAmount',
      title: 'Item Total (AED)',
      value: parseFormatPriceValueFromAPI(sellingPriceAmount)
    },
    {
      dbName: 'deliveryChargeAmount',
      title: 'Delivery Charges (AED)',
      value: parseFormatPriceValueFromAPI(deliveryChargeAmount)
    },
    {
      dbName: 'additionalChargeAmount',
      title: 'Additional Charges (AED)',
      value: parseFormatPriceValueFromAPI(additionalChargeAmount)
    },
    {
      dbName: 'promoCodeDiscountAmount',
      isVisible: Boolean(promoCodeDiscountAmount),
      subTitle: promoCode?.code || '',
      subTitleClass: 'text-green font-medium',
      title: 'Promo',
      value: `-${parseFormatPriceValueFromAPI(promoCodeDiscountAmount)}`,
      valueClass: 'text-green font-medium'
    },
    {
      dbName: 'bulkDiscount',
      isVisible: Boolean(lumpSumDiscount),
      title: 'Bulk Discount',
      value: `- ${parseFormatPriceValueFromAPI(lumpSumDiscount)}`,
      valueClass: 'text-green font-medium'
    },
    {
      dbName: 'preVatAmount',
      title: 'Pre-VAT Total (AED)',
      value: parseFormatPriceValueFromAPI(preVatAmount)
    },
    {
      dbName: 'vatAmount',
      title: 'VAT (5%)',
      value: parseFormatPriceValueFromAPI(vatAmount)
    },
    {
      dbName: 'nonTaxableCharge',
      title: 'Other Charges (non-taxable)',
      value: parseFormatPriceValueFromAPI(nonTaxableCharge),
      show: nonTaxableCharge > 0
    },
    {
      dbName: 'orderTotal',
      title: 'Grand Total (AED)',
      value: parseFormatPriceValueFromAPI(orderTotal)
    }
  ];
  return (
    <CartSummary
      {...{
        code: promoCode?.code,
        isCartEditable,
        isDefaultCart,
        note,
        onProceedForCheckout,
        orderTotal,
        priceSummaryList,
        proceedToCheckoutAndPaymentDisabled,
        processApplyPromoCode,
        processRemovePromoCode,
        promoCodeDiscountAmount,
        promoCodeError,
        renderTapPaymentWidget,
        setLoading,
        setPromoCodeError,
        setRenderTapPaymentWidget,
        setShowToast,
        setTapSanitizedToken,
        tapSanitizedToken,
        userCart
      }}
    />
  );
};

export default CartSummaryTemplate;
